// Compact backtest summary — metric cards only, no charts. Used by the Builder sidebar
// where vertical space is at a premium. The Monitor tab uses the full StrategyReport
// instead, which includes equity chart, histogram, donut, etc.

import type { BacktestResponseDto, ScanResultDto } from "../../api/types";

interface Props {
  result: BacktestResponseDto | null;
  /** Optional placeholder when result is null. Defaults to "Run a backtest to see metrics here." */
  emptyText?: string;
}

export function BacktestSummary({ result, emptyText = "Run a backtest to see metrics here." }: Props) {
  if (!result) {
    return (
      <div style={{ padding: 12, background: "#161b22", borderRadius: 4, fontSize: 12, color: "#8b949e" }}>
        {emptyText}
      </div>
    );
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {result.warnings && result.warnings.length > 0 && (
        <ul style={{ color: "#d29922", fontSize: 11, marginTop: 0, marginBottom: 0, paddingLeft: 16 }}>
          {result.warnings.map((w, i) => <li key={i}>{w}</li>)}
        </ul>
      )}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <ScanCard title="Long" scan={result.long} />
        <ScanCard title="Short" scan={result.short} />
      </div>
      {result.merged && (
        <div style={{ padding: 12, background: "#161b22", borderRadius: 4, fontSize: 12 }}>
          <strong>Merged:</strong>{" "}
          {result.merged.longTrades + result.merged.shortTrades} trades,{" "}
          WR {fmt(result.merged.winRate)}%,{" "}
          totalReturn {fmt(result.merged.totalReturn)}%,{" "}
          PF {fmt(result.merged.profitFactor)},{" "}
          maxDD {fmt(result.merged.maxDD)}%,{" "}
          score <strong>{result.merged.score}</strong>
        </div>
      )}
    </div>
  );
}

export function ScanCard({ title, scan }: { title: string; scan: ScanResultDto | null }) {
  if (!scan) {
    return (
      <div style={{ padding: 12, background: "#161b22", borderRadius: 4, fontSize: 12, color: "#8b949e" }}>
        <div style={{ fontWeight: 600, color: "#c9d1d9", marginBottom: 4 }}>{title}</div>
        <div>No result (insufficient trades or zero conditions)</div>
      </div>
    );
  }
  return (
    <div style={{ padding: 12, background: "#161b22", borderRadius: 4, fontSize: 12 }}>
      <div style={{ fontWeight: 600, marginBottom: 6 }}>
        {title} &middot; score <span style={{ color: "#58a6ff" }}>{scan.score}</span>
      </div>
      <Row k="Trades" v={`${scan.trades} (${scan.wins}W ${scan.losses}L)`} />
      <Row k="Win rate" v={`${fmt(scan.winRate)}%`} />
      <Row k="Avg return" v={`${fmt(scan.avgReturn)}%`} />
      <Row k="Total return" v={`${fmt(scan.totalReturn)}%`} />
      <Row k="Profit factor" v={fmt(scan.profitFactor)} />
      <Row k="Sharpe" v={fmt(scan.sharpe)} />
      <Row k="Max DD" v={`${fmt(scan.maxStratDD)}%`} />
      <Row k="Suggested SL" v={scan.suggestedSL == null ? "—" : `${fmt(scan.suggestedSL)}%`} />
      <Row k="Suggested TP" v={scan.suggestedTP == null ? "—" : `${fmt(scan.suggestedTP)}%`} />
    </div>
  );
}

function Row({ k, v }: { k: string; v: string | number }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "1px 0" }}>
      <span style={{ color: "#8b949e" }}>{k}</span>
      <span>{v}</span>
    </div>
  );
}

function fmt(n: number): string {
  if (!Number.isFinite(n)) return "—";
  return n.toFixed(2);
}
